const SESSION_KEY = 'surf_analytics_session_id';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export type AnalyticsEventType =
  | 'site_enter'
  | 'page_view'
  | 'school_selected'
  | 'date_selected'
  | 'selfie_uploaded'
  | 'photos_found'
  | 'photos_not_found'
  | 'add_to_cart'
  | 'checkout_started'
  | 'payment_completed'
  | 'download_completed';

export interface AnalyticsEventPayload {
  event_type: AnalyticsEventType;
  session_id: string;
  user_id?: string;
  school?: string;
  folder?: string;
  session_date?: string;
  business_line?: string;
  metadata?: Record<string, unknown>;
  language?: string;
  device_type?: string;
  country?: string;
}

let queue: AnalyticsEventPayload[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sessionInitialized = false;
let lastInitKey = '';

function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `sess_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function getAnalyticsSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = generateId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return generateId();
  }
}

export function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

async function initSession(language: string, userId?: string): Promise<void> {
  const key = `${language}|${userId || ''}`;
  if (sessionInitialized && lastInitKey === key) return;
  sessionInitialized = true;
  lastInitKey = key;
  try {
    await fetch(`${API_BASE_URL}/analytics/session/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getAnalyticsSessionId(),
        language,
        device_type: detectDeviceType(),
        user_agent: navigator.userAgent,
        user_id: userId,
      }),
      keepalive: true,
    });
  } catch {
    // no bloquear UX
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushEvents();
  }, 1500);
}

async function flushEvents(): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue.splice(0, 50);
  const body = JSON.stringify({ events: batch });
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(`${API_BASE_URL}/analytics/events`, blob);
      if (ok) return;
    }
    await fetch(`${API_BASE_URL}/analytics/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    queue.unshift(...batch);
  }
}

export function trackEvent(
  eventType: AnalyticsEventType,
  options: {
    school?: string;
    folder?: string;
    session_date?: string;
    metadata?: Record<string, unknown>;
    language?: string;
    user_id?: string;
    business_line?: string;
  } = {},
): void {
  const payload: AnalyticsEventPayload = {
    event_type: eventType,
    session_id: getAnalyticsSessionId(),
    school: options.school,
    folder: options.folder || options.school,
    session_date: options.session_date,
    metadata: options.metadata,
    language: options.language,
    user_id: options.user_id,
    device_type: detectDeviceType(),
    business_line: options.business_line || 'surf_digital',
  };
  queue.push(payload);
  scheduleFlush();
}

export function setupAnalytics(language: string, userId?: string): void {
  void initSession(language, userId);
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    void flushEvents();
  });
}

export function getAnalyticsHeaders(): HeadersInit {
  return { 'X-Analytics-Session-Id': getAnalyticsSessionId() };
}
